import { useEffect, useRef, useState } from 'react';

interface UseAuthSyncOptions {
	userId?: number;
	onLogout: () => void;
}

// The stored user id doubles as the cross-tab signal: writing it fires a `storage`
// event in every OTHER tab on this origin, which is how they learn the session ended.
const UID_KEY = 'uc-agent-uid';

// Must match THREAD_STORAGE_KEY in RuntimeProvider.tsx — the only client-side handle
// on a previous conversation.
const THREAD_KEY = 'uc-agent-thread-id';

function safeGet( key: string ): string | null {
	try {
		return localStorage.getItem( key );
	} catch {
		return null;
	}
}

function safeSet( key: string, value: string ): void {
	try {
		localStorage.setItem( key, value );
	} catch {
		// Storage can be unavailable (Safari private mode, blocked cookies). Fail open.
	}
}

// Drop everything that would give a signed-out browser access to the previous user's
// chats: the thread handle, and the cached user-scoped JWT (valid for up to 15 minutes
// after logout, since it is only refreshed on expiry).
function clearAgentSession(): void {
	try {
		sessionStorage.removeItem( THREAD_KEY );
	} catch {
		// Ignore — see safeSet.
	}

	if ( window.agentConfig ) {
		window.agentConfig.jwtToken = '';
	}
}

// Keeps the AI Search panel in step with the WordPress session across tabs.
//
// Fires only when the previously seen user id was non-zero and has changed — i.e. a
// logout or a user switch. A guest signing in (0 -> id) is deliberately ignored so the
// conversation they started before signing in survives the login redirect.
const useAuthSync = ( { userId, onLogout }: UseAuthSyncOptions ) => {
	const [ loggedOut, setLoggedOut ] = useState( false );
	const uid = String( userId ?? 0 );
	const onLogoutRef = useRef( onLogout );

	useEffect( () => {
		onLogoutRef.current = onLogout;
	}, [ onLogout ] );

	useEffect( () => {
		const previousUid = safeGet( UID_KEY );

		if ( previousUid && previousUid !== '0' && previousUid !== uid ) {
			clearAgentSession();
		}

		safeSet( UID_KEY, uid );
	}, [ uid ] );

	useEffect( () => {
		const handleStorage = ( event: StorageEvent ) => {
			// `newValue === null` means the key was removed or site data was cleared,
			// which is not a logout. The value check matters because Safari has fired
			// this event for same-value writes, and a plain reload in another tab must
			// not close this one's panel.
			if (
				event.key !== UID_KEY
				|| event.newValue === null
				|| event.newValue === uid
				|| uid === '0'
			) {
				return;
			}

			clearAgentSession();
			onLogoutRef.current();
			setLoggedOut( true );
		};

		window.addEventListener( 'storage', handleStorage );

		return () => {
			window.removeEventListener( 'storage', handleStorage );
		};
	}, [ uid ] );

	return { loggedOut };
};

export default useAuthSync;
